import {
  Component, forwardRef, Input, OnDestroy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors } from '@angular/forms';

export interface TagRow {
  key: string;
  values: string[];
}

export interface TagRowState {
  keyTouched: boolean;
  keyDirty: boolean;
  valuesTouched: boolean;
  valuesDirty: boolean;
}

export interface TagRowErrors {
  keyRequired: boolean;
  keyDuplicate: boolean;
  valuesRequired: boolean;
}

@Component({
  selector: 'app-key-value-tag-editor',
  templateUrl: './key-value-tag-editor.component.html',
  styleUrls: ['./key-value-tag-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KeyValueTagEditorComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => KeyValueTagEditorComponent),
      multi: true
    }
  ]
})
export class KeyValueTagEditorComponent implements ControlValueAccessor, Validator, OnDestroy {
  @Input() disabled = false;

  rows: TagRow[] = [];
  rowStates: TagRowState[] = [];
  rowErrors: TagRowErrors[] = [];
  inputTexts: string[] = [];

  private formErrors: ValidationErrors | null = null;
  private onChange: (value: TagRow[]) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  writeValue(value: TagRow[]): void {
    this.rows = value && value.length ? value.map(r => ({ key: r.key, values: [...r.values] })) : [];
    this.inputTexts = this.rows.map(() => '');
    this.rowStates = this.rows.map(() => ({
      keyTouched: false, keyDirty: false,
      valuesTouched: false, valuesDirty: false
    }));
    this.revalidate();
  }

  registerOnChange(fn: (value: TagRow[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.formErrors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  addRow(): void {
    this.rows.push({ key: '', values: [] });
    this.inputTexts.push('');
    this.rowStates.push({
      keyTouched: false, keyDirty: false,
      valuesTouched: false, valuesDirty: false
    });
    this.emitChange();
  }

  removeRow(index: number): void {
    this.rows.splice(index, 1);
    this.inputTexts.splice(index, 1);
    this.rowStates.splice(index, 1);
    this.emitChange();
  }

  onKeyChange(index: number, value: string): void {
    this.rows[index].key = value;
    this.rowStates[index].keyDirty = true;
    this.emitChange();
  }

  onKeyBlur(index: number): void {
    this.rowStates[index].keyTouched = true;
    this.revalidate();
  }

  onValueInputKeydown(event: KeyboardEvent, rowIndex: number): void {
    var text = (this.inputTexts[rowIndex] || '').trim();

    if (event.key === 'Enter' || event.key === 'Tab') {
      if (text) {
        event.preventDefault();
        this.addPill(rowIndex, text);
      }
    } else if (event.key === 'Backspace' && !text) {
      var values = this.rows[rowIndex].values;
      if (values.length > 0) {
        values.pop();
        this.rowStates[rowIndex].valuesDirty = true;
        this.emitChange();
      }
    }
  }

  onValueBlur(rowIndex: number): void {
    this.rowStates[rowIndex].valuesTouched = true;
    var text = (this.inputTexts[rowIndex] || '').trim();
    if (text) {
      this.addPill(rowIndex, text);
    } else {
      this.revalidate();
    }
  }

  addPill(rowIndex: number, text: string): void {
    var trimmed = text.trim();
    if (!trimmed) {
      return;
    }
    var values = this.rows[rowIndex].values;
    if (values.indexOf(trimmed) === -1) {
      values.push(trimmed);
      this.inputTexts[rowIndex] = '';
      this.rowStates[rowIndex].valuesDirty = true;
      this.emitChange();
    } else {
      this.inputTexts[rowIndex] = '';
    }
  }

  removePill(rowIndex: number, valueIndex: number): void {
    this.rows[rowIndex].values.splice(valueIndex, 1);
    this.rowStates[rowIndex].valuesDirty = true;
    this.emitChange();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private revalidate(): void {
    var seenKeys: {[key: string]: boolean} = {};
    var hasRequired = false;
    var hasDuplicate = false;

    this.rowErrors = this.rows.map(function(row, index) {
      var state = this.rowStates[index];
      var interacted = state && (state.keyTouched || state.keyDirty);

      var keyRequired = interacted && !row.key;
      var keyDuplicate = false;
      if (row.key) {
        keyDuplicate = !!seenKeys[row.key];
        seenKeys[row.key] = true;
      }

      var valuesInteracted = state && (state.valuesTouched || state.valuesDirty);
      var valuesRequired = valuesInteracted && (!row.values || row.values.length === 0);

      if (!row.key || !row.values || row.values.length === 0) {
        hasRequired = true;
      }
      if (keyDuplicate) {
        hasDuplicate = true;
      }

      return { keyRequired: keyRequired, keyDuplicate: keyDuplicate, valuesRequired: valuesRequired };
    }.bind(this));

    if (!this.rows || this.rows.length === 0) {
      this.formErrors = null;
    } else if (hasRequired) {
      this.formErrors = { required: true };
    } else if (hasDuplicate) {
      this.formErrors = { duplicateKey: true };
    } else {
      this.formErrors = null;
    }
  }

  private emitChange(): void {
    this.revalidate();
    this.onTouched();
    this.onChange([...this.rows]);
    this.onValidatorChange();
  }

  ngOnDestroy(): void {}
}
